/**
 * @license
 * Copyright Google LLC All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.dev/license
 */

import {forwardRef, resolveForwardRef} from '../../di/forward_ref';
import {ɵɵinject, ɵɵinvalidFactoryDep} from '../../di/injector_compatibility';
import {ɵɵdefineInjectable, ɵɵdefineInjector} from '../../di/interface/defs';
import {registerNgModuleType} from '../../linker/ng_module_registration';
import * as iframe_attrs_validation from '../../sanitization/iframe_attrs_validation';
import * as sanitization from '../../sanitization/sanitization';
import * as r3 from '../index';

/**
 * A mapping of the @angular/core API surface used in generated expressions to the actual symbols.
 *
 * This should be kept up to date with the public exports of @angular/core.
 */
export const angularCoreEnv: {[name: string]: unknown} = (() => ({
  'ɵɵanimateEnter': r3.ɵɵanimateEnter,
  'ɵɵanimateEnterListener': r3.ɵɵanimateEnterListener,
  'ɵɵanimateLeave': r3.ɵɵanimateLeave,
  'ɵɵanimateLeaveListener': r3.ɵɵanimateLeaveListener,
  'ɵɵattribute': r3.ɵɵattribute,
  'ɵɵdefineComponent': r3.ɵɵdefineComponent,
  'ɵɵdefineDirective': r3.ɵɵdefineDirective,
  'ɵɵdefineInjectable': ɵɵdefineInjectable,
  'ɵɵdefineInjector': ɵɵdefineInjector,
  'ɵɵdefineNgModule': r3.ɵɵdefineNgModule,
  'ɵɵdefinePipe': r3.ɵɵdefinePipe,
  'ɵɵdirectiveInject': r3.ɵɵdirectiveInject,
  'ɵɵgetInheritedFactory': r3.ɵɵgetInheritedFactory,
  'ɵɵinject': ɵɵinject,
  'ɵɵinjectAttribute': r3.ɵɵinjectAttribute,
  'ɵɵinvalidFactory': r3.ɵɵinvalidFactory,
  'ɵɵinvalidFactoryDep': ɵɵinvalidFactoryDep,
  'ɵɵtemplateRefExtractor': r3.ɵɵtemplateRefExtractor,
  'ɵɵresetView': r3.ɵɵresetView,
  'ɵɵHostDirectivesFeature': r3.ɵɵHostDirectivesFeature,
  'ɵɵNgOnChangesFeature': r3.ɵɵNgOnChangesFeature,
  'ɵɵControlFeature': r3.ɵɵControlFeature,
  'ɵɵProvidersFeature': r3.ɵɵProvidersFeature,
  'ɵɵInheritDefinitionFeature': r3.ɵɵInheritDefinitionFeature,
  'ɵɵExternalStylesFeature': r3.ɵɵExternalStylesFeature,
  'ɵɵnextContext': r3.ɵɵnextContext,
  'ɵɵnamespaceHTML': r3.ɵɵnamespaceHTML,
  'ɵɵnamespaceMathML': r3.ɵɵnamespaceMathML,
  'ɵɵnamespaceSVG': r3.ɵɵnamespaceSVG,
  'ɵɵenableBindings': r3.ɵɵenableBindings,
  'ɵɵdisableBindings': r3.ɵɵdisableBindings,
  'ɵɵelementStart': r3.ɵɵelementStart,
  'ɵɵelementEnd': r3.ɵɵelementEnd,
  'ɵɵelement': r3.ɵɵelement,
  'ɵɵelementContainerStart': r3.ɵɵelementContainerStart,
  'ɵɵelementContainerEnd': r3.ɵɵelementContainerEnd,
  'ɵɵdomElement': r3.ɵɵdomElement,
  'ɵɵdomElementStart': r3.ɵɵdomElementStart,
  'ɵɵdomElementEnd': r3.ɵɵdomElementEnd,
  'ɵɵdomElementContainer': r3.ɵɵdomElementContainer,
  'ɵɵdomElementContainerStart': r3.ɵɵdomElementContainerStart,
  'ɵɵdomElementContainerEnd': r3.ɵɵdomElementContainerEnd,
  'ɵɵdomTemplate': r3.ɵɵdomTemplate,
  'ɵɵdomListener': r3.ɵɵdomListener,
  'ɵɵelementContainer': r3.ɵɵelementContainer,
  'ɵɵpureFunction0': r3.ɵɵpureFunction0,
  'ɵɵpureFunction1': r3.ɵɵpureFunction1,
  'ɵɵpureFunction2': r3.ɵɵpureFunction2,
  'ɵɵpureFunction3': r3.ɵɵpureFunction3,
  'ɵɵpureFunction4': r3.ɵɵpureFunction4,
  'ɵɵpureFunction5': r3.ɵɵpureFunction5,
  'ɵɵpureFunction6': r3.ɵɵpureFunction6,
  'ɵɵpureFunction7': r3.ɵɵpureFunction7,
  'ɵɵpureFunction8': r3.ɵɵpureFunction8,
  'ɵɵpureFunctionV': r3.ɵɵpureFunctionV,
  'ɵɵgetCurrentView': r3.ɵɵgetCurrentView,
  'ɵɵrestoreView': r3.ɵɵrestoreView,
  'ɵɵlistener': r3.ɵɵlistener,
  'ɵɵprojection': r3.ɵɵprojection,
  'ɵɵsyntheticHostProperty': r3.ɵɵsyntheticHostProperty,
  'ɵɵsyntheticHostListener': r3.ɵɵsyntheticHostListener,
  'ɵɵpipeBind1': r3.ɵɵpipeBind1,
  'ɵɵpipeBind2': r3.ɵɵpipeBind2,
  'ɵɵpipeBind3': r3.ɵɵpipeBind3,
  'ɵɵpipeBind4': r3.ɵɵpipeBind4,
  'ɵɵpipeBindV': r3.ɵɵpipeBindV,
  'ɵɵprojectionDef': r3.ɵɵprojectionDef,
  'ɵɵdomProperty': r3.ɵɵdomProperty,
  'ɵɵariaProperty': r3.ɵɵariaProperty,
  'ɵɵproperty': r3.ɵɵproperty,
  'ɵɵcontrol': r3.ɵɵcontrol,
  'ɵɵcontrolCreate': r3.ɵɵcontrolCreate,
  'ɵɵpipe': r3.ɵɵpipe,
  'ɵɵqueryRefresh': r3.ɵɵqueryRefresh,
  'ɵɵqueryAdvance': r3.ɵɵqueryAdvance,
  'ɵɵviewQuery': r3.ɵɵviewQuery,
  'ɵɵviewQuerySignal': r3.ɵɵviewQuerySignal,
  'ɵɵloadQuery': r3.ɵɵloadQuery,
  'ɵɵcontentQuery': r3.ɵɵcontentQuery,
  'ɵɵcontentQuerySignal': r3.ɵɵcontentQuerySignal,
  'ɵɵreference': r3.ɵɵreference,
  'ɵɵclassMap': r3.ɵɵclassMap,
  'ɵɵstyleMap': r3.ɵɵstyleMap,
  'ɵɵstyleProp': r3.ɵɵstyleProp,
  'ɵɵclassProp': r3.ɵɵclassProp,
  'ɵɵadvance': r3.ɵɵadvance,
  'ɵɵtemplate': r3.ɵɵtemplate,
  'ɵɵconditional': r3.ɵɵconditional,
  'ɵɵconditionalCreate': r3.ɵɵconditionalCreate,
  'ɵɵconditionalBranchCreate': r3.ɵɵconditionalBranchCreate,
  'ɵɵdefer': r3.ɵɵdefer,
  'ɵɵdeferWhen': r3.ɵɵdeferWhen,
  'ɵɵdeferOnIdle': r3.ɵɵdeferOnIdle,
  'ɵɵdeferOnImmediate': r3.ɵɵdeferOnImmediate,
  'ɵɵdeferOnTimer': r3.ɵɵdeferOnTimer,
  'ɵɵdeferOnHover': r3.ɵɵdeferOnHover,
  'ɵɵdeferOnInteraction': r3.ɵɵdeferOnInteraction,
  'ɵɵdeferOnViewport': r3.ɵɵdeferOnViewport,
  'ɵɵdeferPrefetchWhen': r3.ɵɵdeferPrefetchWhen,
  'ɵɵdeferPrefetchOnIdle': r3.ɵɵdeferPrefetchOnIdle,
  'ɵɵdeferPrefetchOnImmediate': r3.ɵɵdeferPrefetchOnImmediate,
  'ɵɵdeferPrefetchOnTimer': r3.ɵɵdeferPrefetchOnTimer,
  'ɵɵdeferPrefetchOnHover': r3.ɵɵdeferPrefetchOnHover,
  'ɵɵdeferPrefetchOnInteraction': r3.ɵɵdeferPrefetchOnInteraction,
  'ɵɵdeferPrefetchOnViewport': r3.ɵɵdeferPrefetchOnViewport,
  'ɵɵdeferHydrateWhen': r3.ɵɵdeferHydrateWhen,
  'ɵɵdeferHydrateNever': r3.ɵɵdeferHydrateNever,
  'ɵɵdeferHydrateOnIdle': r3.ɵɵdeferHydrateOnIdle,
  'ɵɵdeferHydrateOnImmediate': r3.ɵɵdeferHydrateOnImmediate,
  'ɵɵdeferHydrateOnTimer': r3.ɵɵdeferHydrateOnTimer,
  'ɵɵdeferHydrateOnHover': r3.ɵɵdeferHydrateOnHover,
  'ɵɵdeferHydrateOnInteraction': r3.ɵɵdeferHydrateOnInteraction,
  'ɵɵdeferHydrateOnViewport': r3.ɵɵdeferHydrateOnViewport,
  'ɵɵdeferEnableTimerScheduling': r3.ɵɵdeferEnableTimerScheduling,
  'ɵɵrepeater': r3.ɵɵrepeater,
  'ɵɵrepeaterCreate': r3.ɵɵrepeaterCreate,
  'ɵɵrepeaterTrackByIndex': r3.ɵɵrepeaterTrackByIndex,
  'ɵɵrepeaterTrackByIdentity': r3.ɵɵrepeaterTrackByIdentity,
  'ɵɵcomponentInstance': r3.ɵɵcomponentInstance,
  'ɵɵtext': r3.ɵɵtext,
  'ɵɵtextInterpolate': r3.ɵɵtextInterpolate,
  'ɵɵtextInterpolate1': r3.ɵɵtextInterpolate1,
  'ɵɵtextInterpolate2': r3.ɵɵtextInterpolate2,
  'ɵɵtextInterpolate3': r3.ɵɵtextInterpolate3,
  'ɵɵtextInterpolate4': r3.ɵɵtextInterpolate4,
  'ɵɵtextInterpolate5': r3.ɵɵtextInterpolate5,
  'ɵɵtextInterpolate6': r3.ɵɵtextInterpolate6,
  'ɵɵtextInterpolate7': r3.ɵɵtextInterpolate7,
  'ɵɵtextInterpolate8': r3.ɵɵtextInterpolate8,
  'ɵɵtextInterpolateV': r3.ɵɵtextInterpolateV,
  'ɵɵi18n': r3.ɵɵi18n,
  'ɵɵi18nAttributes': r3.ɵɵi18nAttributes,
  'ɵɵi18nExp': r3.ɵɵi18nExp,
  'ɵɵi18nStart': r3.ɵɵi18nStart,
  'ɵɵi18nEnd': r3.ɵɵi18nEnd,
  'ɵɵi18nApply': r3.ɵɵi18nApply,
  'ɵɵi18nPostprocess': r3.ɵɵi18nPostprocess,
  'ɵɵresolveWindow': r3.ɵɵresolveWindow,
  'ɵɵresolveDocument': r3.ɵɵresolveDocument,
  'ɵɵresolveBody': r3.ɵɵresolveBody,
  'ɵɵsetComponentScope': r3.ɵɵsetComponentScope,
  'ɵɵsetNgModuleScope': r3.ɵɵsetNgModuleScope,
  'ɵɵregisterNgModuleType': registerNgModuleType,
  'ɵɵgetComponentDepsFactory': r3.ɵɵgetComponentDepsFactory,
  'ɵsetClassDebugInfo': r3.ɵsetClassDebugInfo,
  'ɵɵdeclareLet': r3.ɵɵdeclareLet,
  'ɵɵstoreLet': r3.ɵɵstoreLet,
  'ɵɵarrowFunction': r3.ɵɵarrowFunction,
  'ɵɵreadContextLet': r3.ɵɵreadContextLet,
  'ɵɵattachSourceLocations': r3.ɵɵattachSourceLocations,
  'ɵɵinterpolate': r3.ɵɵinterpolate,
  'ɵɵinterpolate1': r3.ɵɵinterpolate1,
  'ɵɵinterpolate2': r3.ɵɵinterpolate2,
  'ɵɵinterpolate3': r3.ɵɵinterpolate3,
  'ɵɵinterpolate4': r3.ɵɵinterpolate4,
  'ɵɵinterpolate5': r3.ɵɵinterpolate5,
  'ɵɵinterpolate6': r3.ɵɵinterpolate6,
  'ɵɵinterpolate7': r3.ɵɵinterpolate7,
  'ɵɵinterpolate8': r3.ɵɵinterpolate8,
  'ɵɵinterpolateV': r3.ɵɵinterpolateV,

  'ɵɵsanitizeHtml': sanitization.ɵɵsanitizeHtml,
  'ɵɵsanitizeStyle': sanitization.ɵɵsanitizeStyle,
  'ɵɵsanitizeResourceUrl': sanitization.ɵɵsanitizeResourceUrl,
  'ɵɵsanitizeScript': sanitization.ɵɵsanitizeScript,
  'ɵɵvalidateAttribute': sanitization.ɵɵvalidateAttribute,
  'ɵɵsanitizeUrl': sanitization.ɵɵsanitizeUrl,
  'ɵɵsanitizeUrlOrResourceUrl': sanitization.ɵɵsanitizeUrlOrResourceUrl,
  'ɵɵtrustConstantHtml': sanitization.ɵɵtrustConstantHtml,
  'ɵɵtrustConstantResourceUrl': sanitization.ɵɵtrustConstantResourceUrl,

  'forwardRef': forwardRef,
  'resolveForwardRef': resolveForwardRef,

  'ɵɵtwoWayProperty': r3.ɵɵtwoWayProperty,
  'ɵɵtwoWayBindingSet': r3.ɵɵtwoWayBindingSet,
  'ɵɵtwoWayListener': r3.ɵɵtwoWayListener,

  'ɵɵreplaceMetadata': r3.ɵɵreplaceMetadata,
  'ɵɵgetReplaceMetadataURL': r3.ɵɵgetReplaceMetadataURL,
}))();
